import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import {
  X,
  Send,
  Sparkles,
  RotateCcw,
  ChevronRight,
  Bot,
  User,
  Loader2,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SUGGESTED_QUESTIONS = [
  'How do I install the chat widget?',
  'Why isn\'t the AI answering correctly?',
  'How do I add another AI agent?',
  'How do I let Grace take actions like booking meetings?',
  'How do I set up SMS or WhatsApp?',
  'How do I create my first flow?',
];

interface HelpChatProps {
  onClose: () => void;
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex gap-2.5', isUser && 'flex-row-reverse')}>
      {/* Avatar */}
      <div
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
          isUser ? 'bg-accent text-white' : 'bg-accent/10 text-accent',
        )}
      >
        {isUser ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
      </div>

      {/* Bubble */}
      <div
        className={cn(
          'max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
          isUser
            ? 'bg-accent text-white rounded-tr-sm'
            : 'bg-surface-2 text-text rounded-tl-sm',
        )}
      >
        {/* Render with basic markdown: bold, newlines, bullets */}
        <MessageContent content={message.content} isUser={isUser} />
      </div>
    </div>
  );
}

function MessageContent({ content, isUser }: { content: string; isUser: boolean }) {
  // Split by newline and handle basic markdown inline
  const lines = content.split('\n');
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-1" />;
        // Markdown headers — strip # prefix and render as bold text
        const headingMatch = line.match(/^#{1,6}\s+(.+)/);
        if (headingMatch) {
          return (
            <p key={i} className={cn('font-semibold', isUser ? 'text-white' : 'text-text')}>
              {renderInline(headingMatch[1], isUser)}
            </p>
          );
        }
        // Bullet list items
        if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
          return (
            <div key={i} className="flex gap-1.5">
              <span className={cn('mt-1 flex-shrink-0', isUser ? 'text-white/70' : 'text-text-3')}>•</span>
              <span>{renderInline(line.replace(/^[\s\-•]+/, ''), isUser)}</span>
            </div>
          );
        }
        // Numbered list
        const numMatch = line.match(/^(\d+)\.\s+(.+)/);
        if (numMatch) {
          return (
            <div key={i} className="flex gap-1.5">
              <span className={cn('flex-shrink-0 font-medium', isUser ? 'text-white/80' : 'text-text-3')}>
                {numMatch[1]}.
              </span>
              <span>{renderInline(numMatch[2], isUser)}</span>
            </div>
          );
        }
        return <p key={i}>{renderInline(line, isUser)}</p>;
      })}
    </div>
  );
}

function renderInline(text: string, isUser: boolean): React.ReactNode {
  // Handle **bold**
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={i} className={isUser ? 'text-white font-semibold' : 'text-text font-semibold'}>
              {part.slice(2, -2)}
            </strong>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function TypingDots() {
  return (
    <div className="flex gap-2.5">
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-accent/10 text-accent">
        <Bot className="w-3.5 h-3.5" />
      </div>
      <div className="bg-surface-2 rounded-2xl rounded-tl-sm px-3.5 py-3 flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-text-3 animate-bounce [animation-delay:0ms]" />
        <span className="w-1.5 h-1.5 rounded-full bg-text-3 animate-bounce [animation-delay:150ms]" />
        <span className="w-1.5 h-1.5 rounded-full bg-text-3 animate-bounce [animation-delay:300ms]" />
      </div>
    </div>
  );
}

export default function HelpChat({ onClose }: HelpChatProps) {
  const { getToken } = useAuth();
  const { organization } = useOrganization();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;

      setError(null);
      const userMessage: Message = { role: 'user', content: trimmed };
      const newMessages = [...messages, userMessage];
      setMessages(newMessages);
      setInput('');
      setStreaming(true);
      setStreamingText('');

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = await getToken();
        const orgId = organization?.id;
        const response = await fetch(`${API_BASE}/api/help/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(orgId ? { 'x-clerk-org-id': orgId } : {}),
          },
          body: JSON.stringify({ messages: newMessages }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';

        if (!reader) throw new Error('No response stream');

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (!raw) continue;

            try {
              const parsed = JSON.parse(raw);
              if (parsed.token) {
                accumulated += parsed.token;
                setStreamingText(accumulated);
              } else if (parsed.done) {
                // Finalize the message
                setMessages((prev) => [
                  ...prev,
                  { role: 'assistant', content: accumulated },
                ]);
                setStreamingText('');
                setStreaming(false);
                return;
              } else if (parsed.error) {
                throw new Error(parsed.error);
              }
            } catch {
              // Skip malformed SSE lines
            }
          }
        }
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        setError('Something went wrong. Please try again.');
        setStreamingText('');
        setStreaming(false);
      }
    },
    [messages, streaming, getToken, organization],
  );

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  function handleReset() {
    abortRef.current?.abort();
    setMessages([]);
    setStreamingText('');
    setStreaming(false);
    setError(null);
    setInput('');
  }

  const showWelcome = messages.length === 0 && !streaming;

  return (
    <div className="flex flex-col h-full bg-bg">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border bg-surface flex-shrink-0">
        <div className="w-8 h-8 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text">ServeGrace Help</p>
          <p className="text-xs text-text-3">Ask anything about the platform</p>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={handleReset}
              className="w-7 h-7 flex items-center justify-center text-text-3 hover:text-text hover:bg-surface-2 rounded-lg transition-colors"
              title="New conversation"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-text-3 hover:text-text hover:bg-surface-2 rounded-lg transition-colors"
            title="Close help"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Welcome state */}
        {showWelcome && (
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-3">
                <Bot className="w-6 h-6 text-accent" />
              </div>
              <p className="text-sm font-semibold text-text mb-1">Hi! How can I help?</p>
              <p className="text-xs text-text-3 leading-relaxed max-w-[200px] mx-auto">
                I know everything about ServeGrace. Ask me anything!
              </p>
            </div>

            {/* Suggested questions */}
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold text-text-3 uppercase tracking-wider px-1">
                Popular questions
              </p>
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-surface hover:bg-surface-2 border border-border hover:border-accent/30 text-left transition-all group"
                >
                  <span className="flex-1 text-xs text-text-2 group-hover:text-text leading-snug">{q}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-text-3 flex-shrink-0 group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Conversation */}
        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} />
        ))}

        {/* Streaming in-progress */}
        {streaming && streamingText && (
          <MessageBubble message={{ role: 'assistant', content: streamingText }} />
        )}

        {/* Typing dots (before first token arrives) */}
        {streaming && !streamingText && <TypingDots />}

        {/* Error */}
        {error && (
          <div className="text-center">
            <p className="text-xs text-accent-3 bg-accent-3/10 rounded-xl px-3 py-2">{error}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-border bg-surface px-3 py-3">
        <div className="flex items-end gap-2 bg-surface-2 rounded-xl border border-border focus-within:border-accent/50 transition-colors px-3 py-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question…"
            rows={1}
            disabled={streaming}
            className="flex-1 bg-transparent text-sm text-text placeholder:text-text-3 resize-none outline-none leading-relaxed max-h-28 overflow-y-auto disabled:opacity-50"
            style={{ minHeight: '20px' }}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || streaming}
            className={cn(
              'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all mb-0.5',
              input.trim() && !streaming
                ? 'bg-accent text-white hover:bg-accent-hover'
                : 'bg-surface-3 text-text-3 cursor-not-allowed',
            )}
          >
            {streaming ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}
          </button>
        </div>
        <p className="text-[10px] text-text-3 text-center mt-1.5">
          Press Enter to send · Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
